import {
  InstrumentSubscriptionDataScope,
  ListedInstrument,
  Loadable,
  getFormatter,
  getInstrumentIdForce,
  getMaxIfDefined,
  getMinIfDefined,
  isDefined,
  mergeObjectInto,
  useSubscribeToInstrumentChanges,
} from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { Config } from '../../../../shared/config/config';
import { useEStream } from '../../../../shared/services/sse/useEStream';
import {
  ProductDetailsBase,
  ProductDetailsBaseMaybe,
  productDetailstStateAtom,
} from '../../states/productDetailsStateAtom';

const shouldFormatFundamentalValue = (value: number | undefined): value is number => {
  return isDefined(value) && value !== -99999.99;
};

// TODO move this to common and unifiy it with the mobile version
export const useProductDetails = (instrument: ListedInstrument | null, source: string) => {
  const [product, setProduct] = useAtom(productDetailstStateAtom(instrument ? getInstrumentIdForce(instrument) : ''));

  const { formatNumberRoundOrF2, formatDateTime } = getFormatter();
  const loadTimerRef = useRef<NodeJS.Timer>(undefined);
  const { subscribeToInstrumentChanges } = useEStream();

  const startTimer = useCallback(() => {
    loadTimerRef.current = setTimeout(() => {
      if (loadTimerRef.current) {
        clearTimeout(loadTimerRef.current);
        loadTimerRef.current = undefined;
      }
      setProduct(Loadable.failed);
    }, Config.NetBroker.Timeouts.ProductDetailsTimeout);
  }, [setProduct]);

  const clearTimer = useCallback(() => {
    if (loadTimerRef.current) {
      clearTimeout(loadTimerRef.current);
      loadTimerRef.current = undefined;
    }
  }, []);

  // Handle instrument change
  useEffect(() => {
    clearTimer();

    if (isDefined(instrument)) {
      setProduct(Loadable.loading);
      startTimer();
    } else {
      setProduct(Loadable.uninitialized);
    }
  }, [clearTimer, instrument, setProduct, startTimer]);

  // Handle LoadableState change
  useEffect(() => {
    if (product.isLoaded) {
      clearTimer();
      return;
    }

    if (product.isLoading && !isDefined(loadTimerRef.current)) {
      startTimer();
    }
  }, [product.isLoaded, product.isLoading, clearTimer, startTimer]);

  useSubscribeToInstrumentChanges({
    source,
    instrument,
    subscribeToInstrumentChanges,
    dataScope: [InstrumentSubscriptionDataScope.MarketData, InstrumentSubscriptionDataScope.Fundamentals],
    forceRequestStaticData: true,
    onChange(changedItem) {
      if (changedItem && changedItem.price?.value !== undefined && changedItem.details) {
        setProduct((prevProductDetails: ProductDetailsBaseMaybe) => {
          if (changedItem.price?.value === undefined || changedItem.closePrice === undefined || !changedItem.details) {
            return prevProductDetails;
          }

          const prevDailyLow = prevProductDetails.isLoaded ? prevProductDetails.lowNumber : undefined;
          const prevDailyHigh = prevProductDetails.isLoaded ? prevProductDetails.highNumber : undefined;

          const dailyMinIfDefined = getMinIfDefined(prevDailyLow, changedItem.details.lowDaily);
          const changedItemLow = dailyMinIfDefined ? formatNumberRoundOrF2(dailyMinIfDefined) : '-';

          const dailyMaxIfDefined = getMaxIfDefined(prevDailyHigh, changedItem.details.highDaily);
          const changedItemHigh = dailyMaxIfDefined ? formatNumberRoundOrF2(dailyMaxIfDefined) : '-';

          const newDailyLow = dailyMinIfDefined && prevDailyLow && dailyMinIfDefined < prevDailyLow;
          const newDailyHigh = dailyMaxIfDefined && prevDailyHigh && dailyMaxIfDefined > prevDailyHigh;

          const updates: ProductDetailsBase = {
            instrumentStoreItem: structuredClone(changedItem),
            previousLastPrice: prevProductDetails.isLoaded ? prevProductDetails.lastPrice : undefined,
            lastPrice: changedItem.price.value ? formatNumberRoundOrF2(changedItem.price.value) : '-',
            lastPriceTime: changedItem.lastTradeTime ? formatDateTime(changedItem.lastTradeTime) : '-',
            high: changedItemHigh,
            low: changedItemLow,
            newHighOrLowFlag: !!newDailyLow || !!newDailyHigh,
            lowNumber: dailyMinIfDefined,
            highNumber: dailyMaxIfDefined,
            averagePrice: changedItem.details.averagePrice
              ? formatNumberRoundOrF2(changedItem.details.averagePrice)
              : '-',
            closePrice: changedItem.closePrice ? formatNumberRoundOrF2(changedItem.closePrice) : '-',
            dailyChange: isDefined(changedItem.details?.changeNominal)
              ? formatNumberRoundOrF2(changedItem.details.changeNominal)
              : '-',
            dailyChangePercent: isDefined(changedItem.details.changePercentage)
              ? formatNumberRoundOrF2(changedItem.details?.changePercentage * 100)
              : '-',
            openPrice: changedItem.openPrice ? formatNumberRoundOrF2(changedItem.openPrice) : '-',
            YTDChangePercent: isDefined(changedItem.details.yearToDateChange)
              ? formatNumberRoundOrF2(changedItem.details.yearToDateChange)
              : '-',
            annualLow: changedItem.details.low52Week ? formatNumberRoundOrF2(changedItem.details.low52Week) : '-',
            annualLowDate: changedItem.details.lowDate52Week || '-',
            annualHigh: changedItem.details.high52Week ? formatNumberRoundOrF2(changedItem.details.high52Week) : '-',
            annualHighDate: changedItem.details.highDate52Week || '-',
            volumeQuantity: isDefined(changedItem.details.volumeQuantity)
              ? formatNumberRoundOrF2(changedItem.details.volumeQuantity)
              : '-',
            volumeValue: isDefined(changedItem.details.volumeNominal)
              ? formatNumberRoundOrF2(changedItem.details.volumeNominal)
              : '',
            tenDayAverageVolume: isDefined(changedItem.details.avarage10DayVolume)
              ? formatNumberRoundOrF2(changedItem.details.avarage10DayVolume)
              : '-',
            marketCapitalization: shouldFormatFundamentalValue(changedItem.fundamentals?.marketCap)
              ? formatNumberRoundOrF2(changedItem.fundamentals.marketCap)
              : '-',
            earningsPerShareTTM: shouldFormatFundamentalValue(changedItem.fundamentals?.eps_ttm)
              ? formatNumberRoundOrF2(changedItem.fundamentals.eps_ttm)
              : '-',
            priceToEarningsTTM: shouldFormatFundamentalValue(changedItem.fundamentals?.p_e_ttm)
              ? formatNumberRoundOrF2(changedItem.fundamentals.p_e_ttm)
              : '-',
            priceToBookValue: shouldFormatFundamentalValue(changedItem.fundamentals?.p_bv)
              ? formatNumberRoundOrF2(changedItem.fundamentals.p_bv)
              : '-',
            priceToSalesTTM: shouldFormatFundamentalValue(changedItem.fundamentals?.p_s_ttm)
              ? formatNumberRoundOrF2(changedItem.fundamentals.p_s_ttm)
              : '-',
            dividend: shouldFormatFundamentalValue(changedItem.fundamentals?.dividend)
              ? formatNumberRoundOrF2(changedItem.fundamentals.dividend)
              : '-',
            dividendYield: shouldFormatFundamentalValue(changedItem.fundamentals?.dividendYield)
              ? formatNumberRoundOrF2(changedItem.fundamentals.dividendYield)
              : '-',
            dividendYield5Years:
              shouldFormatFundamentalValue(changedItem.fundamentals?.dividendYield5YAvg) &&
              changedItem.fundamentals.dividendYield5YAvg >= 0
                ? formatNumberRoundOrF2(changedItem.fundamentals.dividendYield5YAvg)
                : '-',
            EBITDA: shouldFormatFundamentalValue(changedItem.fundamentals?.ebitda_ttm)
              ? formatNumberRoundOrF2(changedItem.fundamentals.ebitda_ttm)
              : '-',
            ROE: shouldFormatFundamentalValue(changedItem.fundamentals?.roe_ttm)
              ? formatNumberRoundOrF2(changedItem.fundamentals.roe_ttm)
              : '-',
            ROA: shouldFormatFundamentalValue(changedItem.fundamentals?.roa_ttm)
              ? formatNumberRoundOrF2(changedItem.fundamentals.roa_ttm)
              : '-',
          };

          return mergeObjectInto<ProductDetailsBaseMaybe>({ ...prevProductDetails }, Loadable.loaded(updates));
        });
      }
    },
  });

  return {
    product,
  };
};
